import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from './storage';

export interface Property {
  id: number;
  tenant_id?: number; // Backend alignment
  name: string;
  description?: string; // Backend has this field
  property_type_id: number; // Use backend property_type_id
  property_type?: {
    // Optional property type object for display
    id: number;
    name: string;
    label: string;
  };
  stars?: number; // Backend allows null
  location?: string;
  contract_start?: string; // Backend field names
  contract_end?: string;
  is_active: boolean; // Backend field name
  created_at?: string; // Backend timestamps
  updated_at?: string;

  // Frontend-specific fields (not in backend)
  lastScore?: number;
  logoUrl?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  departments?: string[]; // These come from separate tables in backend
  touchpoints?: string[];
  totalAudits?: number;
  lastAuditDate?: string;
  averageScore?: number;
}

interface PropertiesState {
  properties: Property[];
  selectedProperty: Property | null;
  activePropertyTab: 'overview' | 'assessments' | 'departments' | 'contact';
  filters: {
    type: string[];
    status: 'all' | 'active' | 'inactive';
    stars: number[];
    location: string;
  };
  sortBy: 'name' | 'lastScore' | 'lastAuditDate' | 'location';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  isLoading: boolean;
  lastUpdated: string | null;
}

const initialState: PropertiesState = {
  properties: [
    {
      id: 1,
      tenant_id: 1,
      name: 'Grand Hyatt Dubai',
      description: 'Luxury 5-star hotel in the heart of Dubai',
      property_type_id: 1, // Hotel property type
      stars: 5,
      location: 'Dubai, UAE',
      contract_start: '2024-01-01',
      contract_end: '2025-12-31',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z',
      lastScore: 92,
      logoUrl: 'https://picsum.photos/200/200?random=1',
      contactPerson: 'Ahmed Al-Mansouri',
      contactEmail: 'ahmed.mansouri@grandhyatt.com',
      contactPhone: '+971-4-317-1234',
      address: 'Sheikh Zayed Road, Dubai, UAE',
      departments: [
        'Front of House',
        'Housekeeping',
        'Food & Beverage',
        'Maintenance',
      ],
      touchpoints: ['Check-In', 'Concierge', 'Room Service', 'Restaurant'],
      totalAudits: 24,
      lastAuditDate: '2024-08-15T10:30:00Z',
      averageScore: 89,
    },
    {
      id: 2,
      tenant_id: 1,
      name: 'Atlantis The Palm',
      description: "Iconic resort on Dubai's Palm Jumeirah",
      property_type_id: 2, // Resort property type
      stars: 5,
      location: 'Palm Jumeirah, Dubai',
      contract_start: '2024-01-01',
      contract_end: '2025-12-31',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z',
      lastScore: 88,
      logoUrl: 'https://picsum.photos/200/200?random=2',
      contactPerson: 'Sarah Johnson',
      contactEmail: 'sarah.johnson@atlantis.com',
      contactPhone: '+971-4-426-2000',
      address: 'Crescent Road, Palm Jumeirah, Dubai',
      departments: [
        'Front of House',
        'Housekeeping',
        'Food & Beverage',
        'Recreation',
      ],
      touchpoints: ['Check-In', 'Spa', 'Aquaventure', 'Restaurants'],
      totalAudits: 18,
      lastAuditDate: '2024-08-20T14:15:00Z',
      averageScore: 86,
    },
    {
      id: 3,
      tenant_id: 2,
      name: 'Nobu Restaurant Dubai',
      description: 'Premium Japanese dining experience',
      property_type_id: 3, // Restaurant property type
      stars: 4,
      location: 'Downtown Muscat, Oman',
      contract_start: '2024-02-01',
      contract_end: '2026-01-31',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z',
      lastScore: 95,
      logoUrl: 'https://picsum.photos/200/200?random=3',
      contactPerson: 'Takeshi Yamamoto',
      contactEmail: 'takeshi.yamamoto@nobu.com',
      contactPhone: '+971-4-506-0100',
      address: 'Four Seasons Resort Dubai at Jumeirah Beach',
      departments: ['Kitchen', 'Service', 'Bar', 'Reception'],
      touchpoints: ['Greeting', 'Seating', 'Food Service', 'Payment'],
      totalAudits: 12,
      lastAuditDate: '2024-07-30T19:00:00Z',
      averageScore: 93,
    },
    {
      id: 4,
      tenant_id: 3,
      name: 'Emirates Hills Villa Complex',
      description: 'Luxury residential compound with premium services',
      property_type_id: 4, // Compound property type
      location: 'Silicon Oasis, Dubai',
      contract_start: '2024-03-01',
      contract_end: '2025-02-28',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z',
      lastScore: 87,
      logoUrl: 'https://picsum.photos/200/200?random=4',
      contactPerson: 'Michael Roberts',
      contactEmail: 'michael.roberts@emirateshills.ae',
      contactPhone: '+971-4-375-7777',
      address: 'Emirates Hills, Dubai, UAE',
      departments: ['Security', 'Maintenance', 'Landscaping', 'Concierge'],
      touchpoints: [
        'Gate Entry',
        'Concierge',
        'Maintenance Request',
        'Common Areas',
      ],
      totalAudits: 8,
      lastAuditDate: '2024-06-25T11:00:00Z',
      averageScore: 85,
    },
    {
      id: 5,
      tenant_id: 1,
      name: 'Burj Al Arab Jumeirah',
      description: "World's most luxurious hotel",
      property_type_id: 1, // Hotel property type
      stars: 7,
      location: 'Jumeirah, Dubai',
      contract_start: '2024-01-01',
      contract_end: '2025-12-31',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z',
      lastScore: 97,
      logoUrl: 'https://picsum.photos/200/200?random=5',
      contactPerson: 'Fatima Al-Zahra',
      contactEmail: 'fatima.alzahra@jumeirah.com',
      contactPhone: '+971-4-301-7777',
      address: 'Jumeirah St, Dubai, UAE',
      departments: ['Front of House', 'Butler Service', 'Fine Dining', 'Spa'],
      touchpoints: [
        'Helicopter Arrival',
        'Butler Service',
        'Al Muntaha',
        'Talise Spa',
      ],
      totalAudits: 36,
      lastAuditDate: '2024-09-01T08:00:00Z',
      averageScore: 96,
    },
  ],
  selectedProperty: null,
  activePropertyTab: 'overview',
  filters: {
    type: [],
    status: 'all',
    stars: [],
    location: '',
  },
  sortBy: 'name',
  sortOrder: 'asc',
  searchQuery: '',
  isLoading: false,
  lastUpdated: new Date().toISOString(),
};

const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    setProperties: (state, action: PayloadAction<Property[]>) => {
      state.properties = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    addProperty: (state, action: PayloadAction<Property>) => {
      state.properties.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },
    updateProperty: (
      state,
      action: PayloadAction<{ id: number; updates: Partial<Property> }>
    ) => {
      const index = state.properties.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.properties[index] = {
          ...state.properties[index],
          ...action.payload.updates,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },
    deleteProperty: (state, action: PayloadAction<number>) => {
      state.properties = state.properties.filter(p => p.id !== action.payload);
      state.lastUpdated = new Date().toISOString();
    },
    setSelectedProperty: (state, action: PayloadAction<Property | null>) => {
      state.selectedProperty = action.payload;
    },
    setPropertyActiveTab: (
      state,
      action: PayloadAction<
        'overview' | 'assessments' | 'departments' | 'contact'
      >
    ) => {
      state.activePropertyTab = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<PropertiesState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {
        type: [],
        status: 'all',
        stars: [],
        location: '',
      };
    },
    setSortBy: (state, action: PayloadAction<PropertiesState['sortBy']>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (
      state,
      action: PayloadAction<PropertiesState['sortOrder']>
    ) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updatePropertyScore: (
      state,
      action: PayloadAction<{ id: number; score: number; auditDate: string }>
    ) => {
      const property = state.properties.find(p => p.id === action.payload.id);
      if (property) {
        property.lastScore = action.payload.score;
        property.lastAuditDate = action.payload.auditDate;
        property.totalAudits = (property.totalAudits || 0) + 1;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase('properties/resetToDefaults', () => {
      return initialState;
    });
  },
});

// Force data reset action
const resetToDefaults = () => ({
  type: 'properties/resetToDefaults',
});

export const {
  setProperties,
  addProperty,
  updateProperty,
  deleteProperty,
  setSelectedProperty,
  setPropertyActiveTab,
  setFilters,
  clearFilters,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setLoading,
  updatePropertyScore,
} = propertiesSlice.actions;

export { resetToDefaults };

const propertiesPersistConfig = {
  key: 'properties',
  storage,
  whitelist: [
    'properties',
    'selectedProperty',
    'activePropertyTab',
    'filters',
    'sortBy',
    'sortOrder',
    'lastUpdated',
  ],
};

export const propertiesReducer = persistReducer(
  propertiesPersistConfig,
  propertiesSlice.reducer
);
export default propertiesSlice.reducer;
